import { getModelConfig, resolveModelKey } from '@/config/model-registry';
import { buildClientRiskContext } from '@/lib/risk/client-identity';
import { attachRecreateSession } from './recreate-session';

const removeUndefinedFields = (payload) => {
  Object.keys(payload).forEach((key) => {
    if (payload[key] === undefined) {
      delete payload[key];
    }
  });
};

export function buildGenerationRequest(params) {
  let requestData = {};

  if (typeof params === 'string') {
    requestData = {
      prompt: params,
      n: 1,
      size: '1024x768',
    };
  } else if (typeof params === 'object' && params) {
    if (params.prompt !== undefined) {
      requestData = {
        prompt: params.prompt,
        n: 1,
        size: params.size || '896x512',
        modelName: params.modelName || 'Sora 2',
        modelKey: params.modelKey,
        aspectRatioLabel: params.aspectRatioLabel,
        resolutionLabel: params.resolutionLabel,
        quality: params.quality || 'standard',
        outputCount: params.outputCount,
        duration: params.duration,
        fps: params.fps,
        style: params.style,
        color: params.color,
        lighting: params.lighting,
        composition: params.composition,
        negative_prompt: params.negativePrompt,
        imageUrl: params.imageUrl,
        inputAssets: params.inputAssets,
        image_influence: params.imageInfluence,
        remixTaskId: params.remixTaskId,
        mode: params.mode,
        grokMode: params.grokMode,
        functionMode: params.functionMode,
        privacyUnrestricted: params.privacyUnrestricted,
        noContentFiltering: params.noContentFiltering,
        generateAudio: params.generateAudio,
        contextIrEnabled: params.contextIrEnabled === true,
        seed: params.seed,
        webSearch: params.webSearch,
        imageQuality: params.imageQuality,
        background: params.background,
        moderation: params.moderation,
        generationMode: params.generationMode,
        expectedCredits: params.expectedCredits,
        turnstileToken: params.turnstileToken,
        clientTaskKey: params.clientTaskKey,
        batchGenerationId: params.batchGenerationId,
        batchIndex: params.batchIndex,
        batchSize: params.batchSize,
        creationSessionId: params.creationSessionId,
        creationEntryPoint: params.creationEntryPoint,
        // Public Visibility 单开关：仅当用户在本标签页实际改动过开关时
        // 才由调用方传入 boolean；undefined 会在下方被剔除，未改动的
        // 旧标签页因此不会覆盖服务器最新值。
        communityVisibility: params.communityVisibility,
      };
    } else {
      const prompt = params[0];
      const options = params[1] || {};

      requestData = {
        prompt,
        n: 1,
        size: options.size || '1024x768',
        aspectRatioLabel: options.aspectRatioLabel,
        resolutionLabel: options.resolutionLabel,
        outputCount: options.outputCount,
        duration: options.duration,
        fps: options.fps,
        style: options.style,
        color: options.color,
        lighting: options.lighting,
        composition: options.composition,
        negative_prompt: options.negativePrompt,
        imageUrl: options.imageUrl,
        inputAssets: options.inputAssets,
        image_influence: options.imageInfluence,
        generateAudio: options.generateAudio,
        privacyUnrestricted: options.privacyUnrestricted,
        noContentFiltering: options.noContentFiltering,
        contextIrEnabled: options.contextIrEnabled === true,
        seed: options.seed,
        webSearch: options.webSearch,
        generationMode: options.generationMode,
        expectedCredits: options.expectedCredits,
        turnstileToken: options.turnstileToken,
        clientTaskKey: options.clientTaskKey,
        batchGenerationId: options.batchGenerationId,
        batchIndex: options.batchIndex,
        batchSize: options.batchSize,
        creationSessionId: options.creationSessionId,
        creationEntryPoint: options.creationEntryPoint,
        communityVisibility: options.communityVisibility,
      };
    }
  }

  removeUndefinedFields(requestData);

  if (typeof window !== 'undefined') {
    try {
      requestData = attachRecreateSession(requestData, {
        origin: window.location.origin,
        storage: window.sessionStorage,
      });
    } catch {
      // Browsers that block session storage still receive the server's
      // actionable Recreate-session validation error.
    }
  }

  const riskContext = buildClientRiskContext();
  if (riskContext) {
    requestData.riskContext = riskContext;
  }

  const resolvedModelKey = requestData.modelKey || resolveModelKey(requestData.modelName || requestData.model || '');
  if (resolvedModelKey && !requestData.modelKey) {
    requestData.modelKey = resolvedModelKey;
  }

  const modelConfig = resolvedModelKey ? getModelConfig(resolvedModelKey) : null;
  const apiEndpoint = modelConfig?.type === 'video'
    ? (resolvedModelKey?.startsWith('botgo-vsr-') ? '/api/video/enhance' : '/api/video/generate')
    : '/api/images/generate';

  const sanitizedRequest = {
    ...requestData,
    turnstileToken: requestData.turnstileToken ? `[TOKEN_LENGTH:${requestData.turnstileToken.length}]` : undefined,
    riskContext: requestData.riskContext ? `[RISK_CONTEXT_V${requestData.riskContext.v || 1}]` : undefined,
  };

  return {
    requestData,
    sanitizedRequest,
    apiEndpoint,
  };
}
